import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Logo from '../../assets/img/logo-color.png';
// import ReactToPdf from 'react-to-pdf';
import ReactToPrint from 'react-to-print';
import useFetch from '../../utils/useFetch';
import { get } from '../../api';
import { formatNPR } from '../../utils/currency';
// import QRCode from 'qrcode.react';

// Add print styles
const printStyles = `
  @media print {
    .no-print {
      display: none !important;
    }
    .print-only {
      display: block !important;
    }
    @page {
      margin: 0.5cm;
    }
  }
`;

function Order() {
	const printRef = useRef();
	const { id } = useParams();

	// State to hold order items
	const [orderItems, setOrderItems] = useState([]);
	const [itemsLoading, setItemsLoading] = useState(false);
	const [itemsError, setItemsError] = useState(null);

	// Fetch order summary data
	const summaryApi = `sales/order-summaries`;
	const { data: orderSummary, loading: summaryLoading, error: summaryError } = useFetch(`${summaryApi}/${id}/`);

	useEffect(() => {
		window.scrollTo(0, 0);
	  }, []);

	// Fetch order items data when orderSummary is available
	useEffect(() => {
		if (orderSummary?.id) {
			const fetchItems = async () => {
				setItemsLoading(true);
				setItemsError(null);

				try {
					const response = await get(`sales/order-items/?order=${orderSummary.id}`);
					setOrderItems(response.data);
				} catch (error) {
					setItemsError(error.message);
				} finally {
					setItemsLoading(false);
				}
			};

			fetchItems();
		}
	}, [orderSummary]);

	if (summaryLoading || itemsLoading) return <div className="animate-pulse">Loading...</div>;
	if (summaryError) return <p>Error loading invoice: {summaryError}</p>;
	if (itemsError && !orderSummary?.order_items_details?.length) {
		return <p>Error loading items: {itemsError}</p>;
	}

	const lineItems =
		orderItems?.results?.length > 0
			? orderItems.results
			: orderSummary?.order_items_details || [];

	// Generate payment QR code data
	// const qrData = {
	// 	accountNumber: orderSummary?.payment_details?.account_number || '4536487987',
	// 	ifsc: orderSummary?.payment_details?.ifsc || 'SBIN0078159',
	// 	amount: orderSummary?.bill_amount
	// };
	

	

	return (
		<div className="bg-white">
			{/* Add style tag for print styles */}
			<style>{printStyles}</style>
			<div className="max-w-[800px] mx-auto py-6 sm:py-10 px-4 sm:px-6">
				<div className="bg-white rounded-lg shadow-md w-full" ref={printRef}>
					{/* Header */}
					<div className="p-4 sm:p-6">
						<div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-6 sm:mb-8">
							<div className="w-32 sm:w-40 mx-auto sm:mx-0">
								<Link to="/">
									<img src={Logo} alt="Company Logo" className="w-full" />
								</Link>
							</div>
							<div className="text-center sm:text-right">
								<h1 className="text-brand-600 text-xl sm:text-2xl font-semibold mb-2 sm:mb-4">Invoice</h1>
								<div className="text-sm text-gray-600">
									<p>Invoice# {orderSummary?.order_number || '004'}</p>
									<p>Invoice Date: {moment(orderSummary?.created_at).format('MMM DD, YYYY')}</p>
									<p>Due Date: {moment(orderSummary?.created_at).add(7, 'days').format('MMM DD, YYYY')}</p>
								</div>
							</div>
						</div>

						{/* Billing Information */}
						<div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
							<div className="bg-brand-50 p-4 rounded-lg">
								<h3 className="font-semibold mb-2">Billed by</h3>
								<div className="text-sm text-gray-600">
									<p className="mb-1">{orderSummary?.seller_details?.company_name || 'WholesaleDai'}</p>
									<p className="mb-1">{orderSummary?.seller_details?.address || 'Newroad, Kathmandu, Nepal'}</p>
									{/* <p className="mb-1">GSTIN: {orderSummary?.seller_details?.gstin || '29ABCDE1234F2Z5'}</p> */}
									<p>PAN: {orderSummary?.seller_details?.pan || '622433042'}</p>
								</div>
							</div>
							<div className="bg-brand-50 p-4 rounded-lg">
								<h3 className="font-semibold mb-2">Billed to</h3>
								<div className="text-sm text-gray-600">
									<p className="mb-1">{`${orderSummary?.buyer_details?.given_name} ${orderSummary?.buyer_details?.family_name}`}</p>
									<p className="mb-1">{orderSummary?.buyer_details?.address}</p>
									<p className="mb-1">Phone: {orderSummary?.buyer_details?.phone_number}</p>
								</div>
							</div>
						</div>

						{/* Table */}
						<div className="mb-6 sm:mb-8 overflow-x-auto -mx-4 sm:mx-0">
							<div className="min-w-[600px] px-4 sm:px-0">
								<table className="w-full border border-gray-200 table-fixed">
									<thead>
										<tr className="bg-brand-600 text-white">
											<th className="py-2 px-3 text-left w-12">Sr.</th>
											<th className="py-2 px-3 text-left">Product Name</th>
											<th className="py-2 px-3 text-left w-24">SKU</th>
											<th className="py-2 px-3 text-center w-20">Qty</th>
											<th className="py-2 px-3 text-right w-28">Item Price</th>
											<th className="py-2 px-3 text-right w-28">Amount</th>
										</tr>
									</thead>
									<tbody className="divide-y divide-gray-200">
										{lineItems.map((item, index) => (
											<tr key={index} className="text-sm hover:bg-gray-50">
												<td className="py-3 px-3 text-gray-500">{index + 1}</td>
												<td className="py-3 px-3 text-gray-500 break-words">
													{item.inventory_item_details.properties.product_info.name}
												</td>
												<td className="py-3 px-3 text-gray-500">{item.product_sku}</td>
												<td className="py-3 px-3 text-center font-medium">{item.quantity}</td>
												<td className="py-3 px-3 text-right font-medium">{formatNPR(item.price)}</td>
												<td className="py-3 px-3 text-right font-medium text-brand-600">
													{formatNPR(item.bill_amount)}
												</td>
											</tr>
										))}
										<tr className="bg-gray-50">
											<td colSpan="5" className="py-3 px-3 text-right font-semibold">Subtotal</td>
											<td className="py-3 px-3 text-right font-semibold">{formatNPR(orderSummary?.sub_total_amount)}</td>
										</tr>
										<tr>
											<td colSpan="5" className="py-3 px-3 text-right font-semibold">Discount</td>
											<td className="py-3 px-3 text-right font-semibold text-brand-600">-{formatNPR(orderSummary?.discount_amount || 0)}</td>
										</tr>
										<tr>
											<td colSpan="5" className="py-3 px-3 text-right font-semibold">Service Charge (Shipping)</td>
											<td className="py-3 px-3 text-right font-semibold">{formatNPR(orderSummary?.service_charge || 0)}</td>
										</tr>
										<tr className="bg-brand-50">
											<td colSpan="5" className="py-3 px-3 text-right font-bold">Total Amount</td>
											<td className="py-3 px-3 text-right font-bold text-brand-600">{formatNPR(orderSummary?.bill_amount)}</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>

						{orderSummary?.shipping_receipt && (
							<div className="mb-6 sm:mb-8">
								<div className="flex flex-wrap items-center justify-between gap-2 mb-3">
									<h3 className="font-semibold text-gray-800">Shipping receipt</h3>
									{orderSummary?.status && (
										<span className="text-xs font-medium uppercase tracking-wide px-2.5 py-1 rounded-full bg-brand-100 text-brand-800">
											{orderSummary.status.replace(/_/g, ' ')}
										</span>
									)}
								</div>
								<div className="rounded-lg border border-gray-200 overflow-hidden bg-gray-50 max-w-lg">
									<a
										href={orderSummary.shipping_receipt}
										target="_blank"
										rel="noopener noreferrer"
										className="block"
									>
										<img
											src={orderSummary.shipping_receipt}
											alt="Shipping receipt"
											className="w-full h-auto object-contain max-h-[420px] bg-white"
										/>
									</a>
									<p className="text-xs text-gray-500 px-3 py-2 text-center border-t border-gray-200">
										Tap or click image to open full size
									</p>
								</div>
							</div>
						)}

						{/* Terms and Additional Notes */}
						<div className="mt-6 sm:mt-8 text-sm text-gray-600">
							<div className="mb-4">
								<h4 className="font-semibold text-gray-700 mb-2">Terms and Conditions</h4>
								<ol className="list-decimal list-inside space-y-1">
									<li>Please pay within 30 days from the date of invoice.</li>
									<li>Please quote invoice number when making payment.</li>
								</ol>
							</div>
						</div>

						{/* Footer */}
						<div className="mt-6 sm:mt-8 text-sm text-gray-500 text-center border-t pt-4">
							<p>Thank you for your business!</p>
						</div>
					</div>

					{/* Actions - Add no-print class */}
					<div className="bg-gray-50 px-4 sm:px-6 py-4 rounded-b-lg no-print">
						<div className="flex flex-col sm:flex-row justify-center sm:justify-end gap-3 sm:gap-4">
							<ReactToPrint
								trigger={() => (
									<button className="w-full sm:w-auto px-4 py-2 bg-brand-600 text-white rounded-md hover:bg-brand-700 transition-colors">
										Print Invoice
									</button>
								)}
								content={() => printRef.current}
							/>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

export default Order;
